/**
 * editDistanceWithin — bounded optimal-string-alignment distance: true when
 * the edit distance between two strings, counting an adjacent TRANSPOSITION
 * as one edit (the most common real typo — "gmial", "invocies"), is <= max.
 * Early-exits on the length gap and on any all-above-cap row, so it is cheap
 * enough to run across a whole catalog per keystroke. Moved here from
 * services/composio/appSearch.ts (2026-09-09, the search that suggests) so the
 * thread search and the app catalog rescue typos the same way; appSearch
 * re-exports it. Pure.
 */
export function editDistanceWithin(a: string, b: string, max: number): boolean {
  if (a === b) return true;
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > max) return false;
  if (la === 0 || lb === 0) return Math.max(la, lb) <= max;

  let prevPrev = new Array<number>(lb + 1);
  let prev = new Array<number>(lb + 1);
  let curr = new Array<number>(lb + 1);
  for (let j = 0; j <= lb; j++) prev[j] = j;

  for (let i = 1; i <= la; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    const ca = a.charCodeAt(i - 1);
    for (let j = 1; j <= lb; j++) {
      const cost = ca === b.charCodeAt(j - 1) ? 0 : 1;
      let d = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (
        i > 1 && j > 1 &&
        ca === b.charCodeAt(j - 2) &&
        a.charCodeAt(i - 2) === b.charCodeAt(j - 1)
      ) {
        d = Math.min(d, prevPrev[j - 2] + 1); // transposition
      }
      curr[j] = d;
      if (d < rowMin) rowMin = d;
    }
    if (rowMin > max) return false; // whole row above the cap — no way back
    const rotate = prevPrev; prevPrev = prev; prev = curr; curr = rotate;
  }
  return prev[lb] <= max;
}
